import React from 'react';
import { Table, Form } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from "../helpers/Fetchwrapper";
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';

const stickyTableStyles = `
.aging-report-scroll {
    overflow: auto !important;
}
.aging-report-scroll thead th {
    position: sticky !important;
    top: 0 !important;
    z-index: 1021 !important;
}
`;

interface AgingRow {
    customerName: string;
    invoiceNo: string;
    invoiceDate: string;
    dueDate: string;
    creditDays: number;
    lastCollectionDate: string;
    outstandingAmount: number;
    days: number;
    days0_30: number;
    days31_60: number;
    days61_90: number;
    days91_180: number;
    days180_plus: number;
}

interface AgingTotals {
    outstanding_amount: number;
    days_0_30: number;
    days_31_60: number;
    days_61_90: number;
    days_91_180: number;
    days_180_plus: number;
}

const getCurrentDateString = () => new Date().toISOString().split('T')[0];

const CustomerOutstandingAgingReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<AgingRow[]>([]);
    const [totals, setTotals] = useState<AgingTotals | null>(null);
    const [formData, setFormData] = useState({
        idProjects: '',
        asOfDate: getCurrentDateString(),
    });

    const [projects, setProjects] = useState<any[]>([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            setProject(projects[0]);
            setFormData(prev => ({ ...prev, idProjects: projects[0].value }));
        }
    }, [projects]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then(resp => resp.json())
            .then(resp => {
                const projectObj = resp.data.map((item: any) => ({
                    ...item,
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectObj);
            })
            .catch(error => console.error(error));
    };

    const handleProjectChange = (project: any) => {
        setProject(project ?? { label: 'Select Project', value: '' });
        setFormData(prev => ({ ...prev, idProjects: project?.value ?? '' }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/customer-outstanding-aging',
                postRequestOptions(formData)
            );
            const data = await response.json();
            if (data.success) {
                setReportData(data.data || []);
                setTotals(data.totals || null);
            } else {
                setReportData([]);
                setTotals(null);
                (data.errors || []).forEach((err: string) =>
                    toast.error(err, { position: 'bottom-right', autoClose: 4000 })
                );
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    const queryParams = new URLSearchParams(formData as any);

    const headerStyle: React.CSSProperties = {
        position: 'sticky',
        top: 0,
        zIndex: 1021,
        backgroundColor: '#DDD',
        color: '#000',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        fontSize: '0.85rem',
        boxShadow: '0 2px 2px -1px rgba(0,0,0,0.4)',
    };

    const agingHeaderStyle: React.CSSProperties = {
        ...headerStyle,
        backgroundColor: '#DDD',
        textAlign: 'center' as const,
    };

    return (
        <>
            <style>{stickyTableStyles}</style>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Title></Card.Title>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="asOfDate">
                                    <Form.Label>As of Date</Form.Label>
                                    <Form.Control type="date" placeholder="As of Date" name='asOfDate' value={formData.asOfDate} onChange={handleChange}/>
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary w-100 p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                </div>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <a className="btn btn-sm btn-primary w-100 p-3 mb-2" href={`${process.env.REACT_APP_BACKEND}customer_outstanding_aging_report_download?` + queryParams.toString()}><i className='fas fa-download'></i>Excel Download</a>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <Card className="shadow-sm">
                <Card.Body className="p-0">
                    <div className="aging-report-scroll" style={{
                        maxHeight: 'calc(100vh - 280px)',
                        position: 'relative',
                        minHeight: 0,
                    }}>
                        <Table bordered hover size="sm" className="mb-0" style={{ width: '100%', marginBottom: 0 }}>
                            <thead>
                                <tr>
                                    <th style={headerStyle} className="text-center">Customer Name</th>
                                    <th style={headerStyle} className="text-center">Invoice No</th>
                                    <th style={headerStyle} className="text-center">Invoice Date</th>
                                    <th style={headerStyle} className="text-center">Due Date/Credit Days</th>
                                    <th style={headerStyle} className="text-center">Last Collection Date</th>
                                    <th style={headerStyle} className="text-center">Invoice/Outstanding Amount</th>
                                    <th style={headerStyle} className="text-center">Days</th>
                                    <th style={agingHeaderStyle}>0-30 Days</th>
                                    <th style={agingHeaderStyle}>31-60 Days</th>
                                    <th style={agingHeaderStyle}>61-90 Days</th>
                                    <th style={agingHeaderStyle}>91-180 Days</th>
                                    <th style={agingHeaderStyle}>180+ Days</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.length === 0 ? (
                                    <tr>
                                        <td colSpan={12} className="text-center py-4 text-muted">
                                            No data found. Select project and click View Report.
                                        </td>
                                    </tr>
                                ) : (
                                    reportData.map((row, idx) => (
                                        <tr key={idx} className={idx % 2 === 1 ? 'table-light' : ''}>
                                            <td>{row.customerName}</td>
                                            <td className="text-center">{row.invoiceNo}</td>
                                            <td className="text-center">{row.invoiceDate}</td>
                                            <td className="text-center">{row.dueDate} ({row.creditDays}d)</td>
                                            <td className="text-center">{row.lastCollectionDate || '-'}</td>
                                            <td className="text-end fw-semibold">{numberFormat(row.outstandingAmount)}</td>
                                            <td className="text-center">{row.days}</td>
                                            <td className="text-end">{numberFormat(row.days0_30)}</td>
                                            <td className="text-end">{numberFormat(row.days31_60)}</td>
                                            <td className="text-end">{numberFormat(row.days61_90)}</td>
                                            <td className="text-end">{numberFormat(row.days91_180)}</td>
                                            <td className="text-end">{numberFormat(row.days180_plus)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            {totals && reportData.length > 0 && (
                                <tfoot>
                                    <tr className="table-light fw-bold">
                                        <td colSpan={5} className="text-end">Total</td>
                                        <td className="text-end">{numberFormat(totals.outstanding_amount)}</td>
                                        <td></td>
                                        <td className="text-end">{numberFormat(totals.days_0_30)}</td>
                                        <td className="text-end">{numberFormat(totals.days_31_60)}</td>
                                        <td className="text-end">{numberFormat(totals.days_61_90)}</td>
                                        <td className="text-end">{numberFormat(totals.days_91_180)}</td>
                                        <td className="text-end">{numberFormat(totals.days_180_plus)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </Table>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default CustomerOutstandingAgingReport;
